import { Divider } from 'antd';
import { Dictionary, keyBy } from 'lodash';
import { stringify } from 'query-string';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { User } from 'api/models';
import { Project } from 'api/project';
import { Category } from 'api/timesheets/categories';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { TimesheetReportsApi } from 'api/timesheets/reports/api';
import {
  TimesheetReport,
  TimesheetReportSummary,
  TimesheetReportType,
} from 'api/timesheets/reports/types';
import { Switcher } from 'components/atoms/switcher';
import { TableSkeleton } from 'components/atoms/table';
import { stringifyDateRange } from 'components/organisms/projects-report/common/range-picker/transformer';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import {
  BillabilitySummary,
  CategoriesSummaryChart,
  HeaderSummary,
  MetricSummary,
  Wrapper,
} from 'components/organisms/timesheets/common/style';
import { UtilizationTitle } from 'components/organisms/timesheets/common/summary';
import { TimesheetTableWithSummary } from '../common';
import {
  useOverallCategoryConfig,
  useOverallPeopleConfig,
  useOverallProjectConfig,
} from '../table-config';
import { overallReportTabs, ReportParam, TimesheetReportTab } from '../types';
import { useColumnConfig } from '../use-column-config';
import { useReports } from '../use-reports';
import { useSummaryDetails } from '../use-summary-detail';
import { useTab } from '../use-tab';

const TabRow = styled.div`
  padding: var(--spacing-05);
  padding-left: var(--left-spacing);
`;
const TableWrapper = styled.div`
  flex: 1;
  height: 100%;
  padding: 0 var(--spacing-05) var(--spacing-05);
  padding-left: var(--left-spacing);
`;

interface Props {
  summary: TimesheetReportSummary;
  timesheetCategories: Category[];
  dateRange: DateRange;
  projects: Project[];
  configuration: Configuration;
  filterParam: ReportParam;
  userLookup: Dictionary<User>;
}

export const TimesheetOverallReport: FC<Props> = ({
  summary,
  timesheetCategories,
  dateRange,
  projects,
  configuration,
  filterParam,
  userLookup,
}) => {
  const { push } = useHistory();
  const { selectedTab, setSelectedTab } = useTab(overallReportTabs);
  const {
    totalMinutes,
    billableMinutes,
    nonBillableMinutes,
    capacityInMinutes,
    noOfDays,
    utilization,
    categoriesSummary,
  } = useSummaryDetails({
    summary,
    dateRange,
    timesheetCategories,
    configuration,
    userLookup,
  });

  const reportApi = useCallback(() => {
    return TimesheetReportsApi.getReports(
      filterParam.startDate,
      filterParam.endDate,
      filterParam.validFilters,
      filterParam.match,
      selectedTab as unknown as TimesheetReportType,
    );
  }, [filterParam, selectedTab]);

  const { isReportsLoading, timesheetReports, timesheetReportsSummary } =
    useReports(reportApi, projects);

  const categoriesLookup = useMemo(
    () => keyBy(timesheetCategories, 'id'),
    [timesheetCategories],
  );
  const filteredReports = useMemo(
    () =>
      selectedTab === TimesheetReportTab.Category
        ? timesheetReports.filter(
            (x) =>
              x.type === TimesheetReportType.Category &&
              (x.billableMinutes + x.nonBillableMinutes > 0 ||
                (x.category &&
                  !categoriesLookup[x.category.categoryId]?.deleted)),
          )
        : timesheetReports,
    [categoriesLookup, selectedTab, timesheetReports],
  );

  const onNameCellClick = (report: TimesheetReport) => {
    const queryParam = `?${stringify({
      range: stringifyDateRange(dateRange),
    })}`;

    if (report.type === TimesheetReportType.Project) {
      push(
        `timesheets/projects/${report.project?.projectId ?? 0}${queryParam}`,
      );
    }
    if (report.type === TimesheetReportType.User) {
      push(`timesheets/users/${report.user.userId}${queryParam}`);
    }
    if (report.type === TimesheetReportType.Category) {
      push(
        `timesheets/categories/${
          report.category?.categoryId ?? 0
        }${queryParam}`,
      );
    }
  };

  const projectColumnConfig = useOverallProjectConfig({
    noOfRows: filteredReports.length,
    timesheetCategories,
    projects,
    onNameCellClick,
  });

  const peopleColumnConfig = useOverallPeopleConfig({
    noOfRows: filteredReports.length,
    noOfDays,
    capacityInMinutes,
    timesheetCategories,
    onNameCellClick,
    configuration,
  });

  const categoryColumnConfig = useOverallCategoryConfig({
    noOfRows: filteredReports.length,
    timesheetCategories,
    onNameCellClick,
    categoriesLookup,
  });

  const columnConfig = useColumnConfig({
    selectedTab,
    projectColumnConfig,
    peopleColumnConfig,
    categoryColumnConfig,
    taskColumnConfig: null,
    phaseColumnConfig: null,
  });

  return (
    <Wrapper>
      <HeaderSummary>
        <BillabilitySummary
          billableMinutes={billableMinutes}
          nonBillableMinutes={nonBillableMinutes}
          capacityInMinutes={capacityInMinutes}
        />
        <MetricSummary
          title={
            <FormattedMessage id="timesheets.summary.total_time_tracked" />
          }
          value={<FormatTime value={totalMinutes} showHypenIfZero={false} />}
        />
        <MetricSummary
          title={<FormattedMessage id="timesheets.summary.team_capacity" />}
          value={<FormatTime value={capacityInMinutes} />}
        />
        <MetricSummary
          title={<UtilizationTitle />}
          value={`${utilization.toFixed(1)}%`}
        />
        <MetricSummary
          title={<FormattedMessage id="timesheets.summary.categories" />}
          value={<CategoriesSummaryChart categories={categoriesSummary} />}
        />
      </HeaderSummary>
      <Divider style={{ margin: 0 }} />
      <TabRow>
        <Switcher
          value={selectedTab}
          onChange={(e) => {
            setSelectedTab(e.target.value);
          }}
        >
          {overallReportTabs.map((tab) => (
            <Switcher.Button key={tab} value={tab}>
              <FormattedMessage id={`timesheets.reports.tabs.${tab}`} />
            </Switcher.Button>
          ))}
        </Switcher>
      </TabRow>
      <TableWrapper>
        {!isReportsLoading ? (
          <TimesheetTableWithSummary
            columns={columnConfig}
            dataSource={filteredReports}
            summaryDataSource={[timesheetReportsSummary]}
            pageHeaderHeight={80}
          />
        ) : (
          <TableSkeleton columnCount={8} />
        )}
      </TableWrapper>
    </Wrapper>
  );
};
